import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { ExteriorMaterial } from "../materials/exteriorMaterialTypes";
import type { InteriorMaterial } from "../materials/interiorPaletteTypes";

export type WallPlaneExteriorMaterialFinish = {
  readonly finishType: "EXTERIOR";
  readonly finishPalette: ExteriorMaterial;
};

export type WallPlaneInteriorMaterialFinish = {
  readonly finishType: "INTERIOR";
  readonly finishPalette: InteriorMaterial;
};

export type WallPlaneMaterialFinish = WallPlaneExteriorMaterialFinish | WallPlaneInteriorMaterialFinish;

interface WallPlaneAssemblyLayerBase {
  readonly id: string;
  readonly sortIndex: number;
  readonly name?: string;
}

export interface WallPlaneFramingLayer extends WallPlaneAssemblyLayerBase {
  readonly layerType: "FRAMING";
  readonly framingComponentId: ForeignEntityId<"FRAMING_COMPONENT">;
  readonly studSpacing: number;
  readonly topPlateCount: number;
  readonly bottomPlateCount: number;
}

export interface WallPlaneMasonryLayer extends WallPlaneAssemblyLayerBase {
  readonly layerType: "MASONRY";
  readonly masonryComponentId: ForeignEntityId<"MASONRY_COMPONENT">;
  readonly finish?: WallPlaneMaterialFinish;
}

export interface WallPlaneCastInPlaceLayer extends WallPlaneAssemblyLayerBase {
  readonly layerType: "CAST_IN_PLACE";
  readonly cipComponentId: ForeignEntityId<"CIP_COMPONENT">;
  readonly finish?: WallPlaneMaterialFinish;
}

export interface WallPlaneSheathingLayer extends WallPlaneAssemblyLayerBase {
  readonly layerType: "SHEATHING";
  readonly sheathingComponentId: ForeignEntityId<"SHEATHING_COMPONENT">;
  readonly finish?: WallPlaneMaterialFinish;
}

export type WallPlaneAssemblyLayer =
  | WallPlaneFramingLayer
  | WallPlaneMasonryLayer
  | WallPlaneCastInPlaceLayer
  | WallPlaneSheathingLayer;

export interface WallPlaneAssemblyEntity extends DocEntityBase<"WALL_PLANE_ASSEMBLY">, LabelableEntityBase {
  readonly id: PrimaryEntityId<"WALL_PLANE_ASSEMBLY">;
  readonly name: string;
  readonly layers: Table<WallPlaneAssemblyLayer>;
}

export function wallPlaneAssemblyNew(
  name: string,
  layers: Table<WallPlaneAssemblyLayer>,
  id: string = uid(),
  sortIndex: number = 0,
): WallPlaneAssemblyEntity {
  return {
    ...docEntityBaseNew("WALL_PLANE_ASSEMBLY", id),
    name,
    labels: {},
    layers,
    sortIndex,
  };
}
